import React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '../../../../icons/animated';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  rowsPerPage: number;
  handlePageChange: (page: number) => void;
  hasHidePagination?: boolean;
}

const Pagination = ({
  currentPage,
  totalPages,
  totalRecords,
  rowsPerPage,
  handlePageChange,
  hasHidePagination,
}: PaginationProps) => {
  // Compute 1-based record indexes
  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handlePageChange(Number(e.target.value));
  };

  return (
    <div className='flex flex-col gap-3 py-3 px-4 sm:flex-row sm:items-center sm:justify-between'>
      { !hasHidePagination && (
        <span className='text-slate-500 text-[12px] sm:text-[13px] font-medium'>
          Showing{ ' ' }
          <span className='text-slate-800 font-semibold tabular-nums'>{ startRecord }</span> –{ ' ' }
          <span className='text-slate-800 font-semibold tabular-nums'>{ endRecord }</span> of{ ' ' }
          <span className='text-slate-800 font-semibold tabular-nums'>{ totalRecords }</span> records
        </span>
      ) }
      <div className='flex items-center justify-between gap-3 sm:justify-end'>
        <div className='flex items-center gap-1 bg-slate-100/90 p-1 rounded-xl border border-slate-200/50'>
          { /* Previous */ }
          <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className='min-h-8 min-w-8 p-1 rounded-lg text-slate-600 hover:bg-white hover:shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none flex items-center justify-center'
              aria-label="Previous page"
          >
            <ChevronLeftIcon size={16} />
          </button>

          { /* Page Indicator / Select */ }
          <div className='relative group'>
            <select
                value={currentPage}
                onChange={handleSelectChange}
                className='min-h-8 pl-3 pr-7 py-1 text-[12px] font-semibold text-slate-800 bg-white border border-slate-200/60 rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.04)] appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 cursor-pointer'
            >
              { Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <option key={page} value={page}>
                    Page { page }
                  </option>
                ),
              ) }
            </select>
            <div className='absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-slate-600 transition-colors'>
              <ChevronDownIcon size={13} />
            </div>
          </div>

          { /* Next */ }
          <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className='min-h-8 min-w-8 p-1 rounded-lg text-slate-600 hover:bg-white hover:shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none flex items-center justify-center'
              aria-label="Next page"
          >
            <ChevronRightIcon size={16} />
          </button>
        </div>
        <div className='text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 whitespace-nowrap tabular-nums'>
          { totalPages } Pages
        </div>
      </div>
    </div>
  );
};

export default Pagination;
